/**
 * Person and model filters for the admin usage report. The filtered report
 * drives the tables, the spend total and the CSV, so a download matches the
 * screen.
 *
 * The bill knows the vendor, not the model: with a model filter, cost is that
 * model's vendor (all Claude or all GPT models) for the matching people.
 */

import type { UsageCsvInput } from './usageCsv';

export interface UsageFilter {
  person: string;
  model: string;
}

export const ALL_MODELS = '';
export const ALL_PEOPLE = '';

export function modelVendor(model: string | null): 'claude' | 'gpt' | null {
  if (!model) return null;
  if (model.startsWith('anthropic.')) return 'claude';
  if (/^(openai\.)?gpt/.test(model)) return 'gpt';
  return null;
}

export function usageModels(report: UsageCsvInput): string[] {
  const models = new Set<string>();
  for (const r of report.tools) if (r.model) models.add(r.model);
  for (const r of report.portal) models.add(r.model_id);
  return [...models].sort();
}

// CloudTrail and the portal can spell the same email with different case.
export function usagePeople(report: UsageCsvInput): string[] {
  const people = new Map<string, string>();
  const add = (p: string): void => {
    if (!people.has(p.toLowerCase())) people.set(p.toLowerCase(), p);
  };
  report.tools.forEach((r) => add(r.person));
  report.portal.forEach((r) => add(r.actor));
  Object.keys(report.cost_by_vendor ?? {}).forEach(add);
  return [...people.values()].sort((a, b) => a.localeCompare(b));
}

export function isFiltered(filter: UsageFilter): boolean {
  return filter.person !== ALL_PEOPLE || filter.model !== ALL_MODELS;
}

export function filterUsage<T extends UsageCsvInput>(report: T, filter: UsageFilter): T {
  if (!isFiltered(filter)) return report;
  const wanted = filter.person.toLowerCase();
  const personOk = (p: string): boolean =>
    filter.person === ALL_PEOPLE || p.toLowerCase() === wanted;
  const modelOk = (m: string | null): boolean =>
    filter.model === ALL_MODELS || m === filter.model;
  const vendor = filter.model === ALL_MODELS ? null : modelVendor(filter.model);

  const costByVendor: Record<string, Record<string, number>> = {};
  for (const [person, vendors] of Object.entries(report.cost_by_vendor ?? {})) {
    if (!personOk(person)) continue;
    const kept = Object.fromEntries(
      Object.entries(vendors).filter(([v]) => filter.model === ALL_MODELS || v === vendor),
    );
    if (Object.keys(kept).length > 0) costByVendor[person] = kept;
  }
  const costByPerson = Object.fromEntries(
    Object.entries(costByVendor).map(([person, vendors]) => [
      person,
      Object.values(vendors).reduce((a, b) => a + b, 0),
    ]),
  );

  return {
    ...report,
    tools: report.tools.filter((r) => personOk(r.person) && modelOk(r.model)),
    portal: report.portal.filter((r) => personOk(r.actor) && modelOk(r.model_id)),
    cost_by_vendor: costByVendor,
    cost_by_person: costByPerson,
    // Portal and "other" cost are single totals, not per person or model.
    portal_cost: null,
    other_cost: null,
  } as T;
}
